'use client';

import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { FaArrowRight } from 'react-icons/fa6';

import AppointmentForm from '@/app/shared/inquiry/appointment-form';
import { useModal } from '@/app/shared/modal-views/use-modal';
import { MagneticButton } from '@/components/flagship/dom/MagneticButton';
import { SpecularBentoCard } from '@/components/flagship/dom/SpecularBentoCard';
import { TiltCard } from '@/components/flagship/dom/TiltCard';
import { FlagshipSection } from '@/components/flagship/page/FlagshipSection';
import { RevealBatch } from '@/components/flagship/page/RevealBatch';

/**
 * Redesigned feature bento.
 *
 * Same six cells, same translation keys (`FeatureGrid.*`) and same imagery as
 * the deleted `components/about-us/Feature-grid.tsx` — this is a visual
 * replacement, not a content change, so copy stays reviewed and localised.
 *
 * Lives under `shared/` rather than `home/sections/` because both `/` and
 * `/about` render it; `Feature-grid` previously served the same two pages via a
 * `variant` prop that only switched the heading component.
 */
export default function BentoSpecular() {
  const t = useTranslations('FeatureGrid');
  const { closeModal, openModal } = useModal();

  function handleAppointment() {
    closeModal();
    openModal({ view: <AppointmentForm isModal />, customSize: '700px' });
  }

  return (
    <FlagshipSection
      className="py-24 md:py-32"
      tagline={t('contentHeader.tagline')}
      title={t('contentHeader.title')}
      description={t('contentHeader.description')}
    >
      <RevealBatch
        selector="[data-bento-cell]"
        y={44}
        stagger={0.09}
        duration={0.9}
        className="mx-auto grid max-w-6xl grid-cols-1 gap-5 px-6 sm:grid-cols-2 lg:grid-cols-3"
      >
        {/* Playful environment — wide image cell with 3D tilt */}
        <div data-bento-cell className="sm:col-span-2">
          <TiltCard max={6} className="h-full">
            <SpecularBentoCard className="h-full" contentClassName="relative h-[340px] md:h-[400px]">
              <Image
                src="/images/about-us/entertainment-room-01.jpg"
                alt={t('sections.playfulEnvironment.title')}
                fill
                sizes="(max-width: 640px) 100vw, (max-width: 1024px) 100vw, 66vw"
                className="rounded-[25px] object-cover"
              />
              <div className="absolute inset-0 rounded-[25px] bg-gradient-to-t from-black/80 via-black/25 to-transparent" />
              <div
                className="absolute inset-x-0 bottom-0 p-6 text-left"
                // Lifts the caption off the image plane inside TiltCard's 3D space.
                style={{ transform: 'translateZ(38px)' }}
              >
                <h3 className="fg-display text-2xl text-white md:text-3xl">
                  {t('sections.playfulEnvironment.title')}
                </h3>
                <p className="mt-2 max-w-[46ch] font-inter text-sm text-white/80">
                  {t('sections.playfulEnvironment.description')}
                </p>
              </div>
            </SpecularBentoCard>
          </TiltCard>
        </div>

        {/* Stay connected — tall app showcase */}
        <div data-bento-cell className="sm:row-span-2">
          <SpecularBentoCard tone="blue" className="h-full" contentClassName="flex h-full flex-col p-6">
            <h3 className="fg-display fg-ink text-xl">{t('sections.stayConnected.title')}</h3>
            <p className="fg-ink-soft mt-2 font-inter text-sm leading-relaxed">
              {t('sections.stayConnected.description')}
            </p>
            <div className="relative mt-6 flex-1 overflow-hidden">
              <Image
                src="/images/showcase/showcase-2.png"
                alt={t('sections.stayConnected.title')}
                width={300}
                height={600}
                sizes="(max-width: 640px) 80vw, 300px"
                className="absolute bottom-0 left-1/2 w-[240px] -translate-x-1/2 translate-y-[18%] drop-shadow-2xl transition-transform duration-700 ease-out hover:translate-y-[10%]"
                style={{ height: 'auto' }}
              />
            </div>
          </SpecularBentoCard>
        </div>

        {/* Affordable education */}
        <div data-bento-cell>
          <SpecularBentoCard className="h-full" contentClassName="relative h-full min-h-[190px] p-6">
            <h3 className="fg-display fg-ink text-lg">{t('sections.affordableEducation.title')}</h3>
            <p className="fg-ink-soft mt-2 max-w-[24ch] font-inter text-sm leading-relaxed">
              {t('sections.affordableEducation.description')}
            </p>
            <Image
              src="/images/about-us/affordable_education.png"
              alt=""
              aria-hidden="true"
              width={180}
              height={180}
              sizes="180px"
              className="pointer-events-none absolute bottom-0 right-0 w-[130px] opacity-90 lg:w-[150px]"
              style={{ height: 'auto' }}
            />
          </SpecularBentoCard>
        </div>

        {/* Beyond academics */}
        <div data-bento-cell>
          <SpecularBentoCard className="h-full" contentClassName="relative h-full min-h-[190px] p-6">
            <h3 className="fg-display fg-ink text-lg">{t('sections.beyondAcademics.title')}</h3>
            <p className="fg-ink-soft mt-2 max-w-[24ch] font-inter text-sm leading-relaxed">
              {t('sections.beyondAcademics.description')}
            </p>
            <Image
              src="/images/about-us/beyond-academics.png"
              alt=""
              aria-hidden="true"
              width={180}
              height={180}
              sizes="180px"
              className="pointer-events-none absolute bottom-0 right-0 w-[140px] opacity-90 lg:w-[160px]"
              style={{ height: 'auto' }}
            />
          </SpecularBentoCard>
        </div>

        {/* Parent engagement — wide */}
        <div data-bento-cell className="lg:col-span-2">
          <SpecularBentoCard tone="blue" className="h-full" contentClassName="relative h-full min-h-[190px] p-6">
            <h3 className="fg-display fg-ink text-lg">{t('sections.parentEngagement.title')}</h3>
            {/*
              46ch is the WIDE measure, and this cell is only wide at `lg`
              (`lg:col-span-2`). Unconstrained below that, the copy ran the full
              width of a single-column card and straight under the artwork
              anchored bottom-right — on a 390px viewport the last 106px of every
              line was covered, which ate the end of the sentence. The other two
              art cards clear it because they cap at 24ch; match them until the
              cell actually earns the extra width.
            */}
            <p className="fg-ink-soft mt-2 max-w-[24ch] font-inter text-sm leading-relaxed lg:max-w-[46ch]">
              {t('sections.parentEngagement.description')}
            </p>
            <Image
              src="/images/about-us/parent-engagement.png"
              alt=""
              aria-hidden="true"
              width={200}
              height={200}
              sizes="200px"
              className="pointer-events-none absolute bottom-0 right-0 w-[130px] opacity-90 lg:w-[170px]"
              style={{ height: 'auto' }}
            />
          </SpecularBentoCard>
        </div>

        {/* Enrollment CTA */}
        <div data-bento-cell>
          <SpecularBentoCard
            className="h-full"
            contentClassName="flex h-full min-h-[190px] flex-col items-center justify-between p-6 text-center"
          >
            <div>
              <h3 className="fg-display fg-ink text-lg">{t('sections.enrollment.title')}</h3>
              <p className="fg-ink-soft mt-2 font-inter text-sm leading-relaxed">
                {t('sections.enrollment.description')}
              </p>
            </div>
            <MagneticButton
              onClick={handleAppointment}
              icon={<FaArrowRight size={12} />}
              radius={120}
              className="mt-4 px-5 py-3 text-xs"
            >
              {t('sections.enrollment.buttonText')}
            </MagneticButton>
          </SpecularBentoCard>
        </div>
      </RevealBatch>
    </FlagshipSection>
  );
}
